import { Button } from '@talentmesh/ui/components/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@talentmesh/ui/components/dropdown-menu';
import { MoreVertical, RotateCcw, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useCandidatesResultsContext } from '../../Contexts/CandidateResultsContext';
import { useCandidatesClient } from '@/Hooks/ClientHooks';
import isResendInvitationAllowed from '@/Utils/isResendInvitationAllowed';
import isResetAssessmentAllowedState from '@/Utils/isResetAssessmentAllowedState';
import UIStrings from '@/Utils/UIStrings';

interface HeaderActionsProps {
    setOpenAssessmentResetDialog: (value: boolean) => void;
}

function HeaderActions({ setOpenAssessmentResetDialog }: HeaderActionsProps) {
    const candidatesClient = useCandidatesClient();
    const { results, candidateId, assessmentId } = useCandidatesResultsContext();
    const { completedAt, candidateStatus, candidateFirstName, candidateLastName } = results;

    const resendInvitationHandlerAsync = async () => {
        try {
            await candidatesClient.resendInvitationAsync(assessmentId, candidateId);
            toast.success(UIStrings.InvitationHasBeenSentTo(candidateFirstName, candidateLastName));
        } catch {
            toast.error(UIStrings.SomethingWentWrongPleaseTryAgainLater);
        }
    };

    const resetAssessmentHandlerAsync = () => {
        setOpenAssessmentResetDialog(true);
    };

    const resendDisabled = !isResendInvitationAllowed(candidateStatus, completedAt);
    const resetDisabled = results.isReset || !isResetAssessmentAllowedState(candidateStatus);
    const menuItemClassNames = 'gap-2';
    const menuItemIconClassNames = 'text-muted-foreground size-4';

    return (
        <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon-sm" aria-label="More actions" data-cy="menuButton">
                    <MoreVertical className="size-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-64">
                <DropdownMenuItem
                    disabled={resendDisabled}
                    onClick={resendInvitationHandlerAsync}
                    className={menuItemClassNames}
                >
                    <Send className={menuItemIconClassNames} aria-hidden />
                    {UIStrings.SendAssessmentInvitation}
                </DropdownMenuItem>
                <DropdownMenuItem
                    disabled={resetDisabled}
                    onClick={resetAssessmentHandlerAsync}
                    className={menuItemClassNames}
                >
                    <RotateCcw className={menuItemIconClassNames} aria-hidden data-testid="RotateCcwIcon" />
                    {UIStrings.ResetAssessment}
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default HeaderActions;
